import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import {
  LuCalendar,
  LuCircleUser,
  LuFileText,
  LuLayoutGrid,
  LuLayoutList,
  LuLink,
  LuMapPin,
  LuRoute,
  LuSettings,
  LuSquarePlus,
  LuUsers,
} from 'react-icons/lu'

import UserClients from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/UserClients'
import UserCompanies from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/UserCompanies'
import UserGroups from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/UserGroups'
import UserNetworks from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/UserNetworks'
import ActionLink from '@/components/misc/ActionLink'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import useAppSelector from '@/hooks/useAppSelector'
import { useAuth } from '@/hooks/useAuth'
import { faPager } from '@/lib/fontAwesomePro/light'
import { ROUTES } from '@/lib/routes'
import authService from '@/lib/services/authService'
import { cn } from '@/lib/utils'
import {
  selectCompanies,
  selectCurrentCompany,
  selectHasClientAccount,
  selectHasCompanyAccount,
  selectHasGroupAccount,
  selectHasNetworkAccount,
  selectIsCompanyAdmin,
  selectIsCompanyUser,
  selectIsGroupUser,
  selectIsIcaewUser,
} from '@/redux/slices/session'

const AccountMenuDropdown: React.FC = () => {
  const isCompanyUser = useAppSelector(selectIsCompanyUser)
  const isIcaewUser = useAppSelector(selectIsIcaewUser)
  const isGroupUser = useAppSelector(selectIsGroupUser)
  const currentCompany = useAppSelector(selectCurrentCompany)
  const hasGroupAccount = useAppSelector(selectHasGroupAccount)
  const companies = useAppSelector(selectCompanies)
  const hasClientAccount = useAppSelector(selectHasClientAccount)
  const hasNetworkAccount = useAppSelector(selectHasNetworkAccount)
  const isCompanyAdmin = useAppSelector(selectIsCompanyAdmin)
  const { signOut } = useAuth()
  const hasCompanyAccount = useAppSelector(selectHasCompanyAccount)

  return (
    <>
      {hasNetworkAccount && !isIcaewUser && <UserNetworks />}
      {hasGroupAccount && !isIcaewUser && <UserGroups />}
      {hasCompanyAccount && !isIcaewUser && <UserCompanies />}
      {hasClientAccount && !isIcaewUser && <UserClients />}

      {isCompanyUser && (
        <>
          <DropdownLink to={ROUTES.addCompany()}>
            <LuSquarePlus size={16} />
            {companies.length > 0 ? 'Add another Company' : 'Add Company'}
          </DropdownLink>

          <DropdownMenuSeparator />
        </>
      )}

      <DropdownMenuItem asChild>
        <a
          href={authService.getAccountManagementUrl()}
          target="_blank"
          rel="noreferrer"
          className="text-foreground no-underline"
        >
          <LuCircleUser size={16} />
          Profile
        </a>
      </DropdownMenuItem>

      {isCompanyUser && (
        <DropdownLink to={ROUTES.preferences()}>
          <LuSettings size={16} />
          Preferences
        </DropdownLink>
      )}

      {isGroupUser && (
        <DropdownLink to={ROUTES.groupPreferences()}>
          <LuSettings size={16} />
          Preferences
        </DropdownLink>
      )}

      {isCompanyUser && currentCompany && (
        <DropdownLink
          to={ROUTES.completeRegistration()}
          className="items-start"
        >
          <LuFileText size={16} className="mt-0.5" />
          Tell us about {currentCompany.presentationName}
        </DropdownLink>
      )}

      {!!(
        currentCompany?.isFcaFirm &&
        currentCompany?.frontendIdentifier &&
        currentCompany.frontendIdentifierType
      ) && (
        <DropdownLink
          to={ROUTES.companyProfile({
            searchParams: {
              identifier: currentCompany.frontendIdentifier,
              identifierType: currentCompany.frontendIdentifierType,
            },
          })}
        >
          <FontAwesomeIcon icon={faPager} />
          View firm RQ profile
        </DropdownLink>
      )}

      {isCompanyAdmin && (
        <DropdownLink to={ROUTES.manageReferralStatuses()}>
          <LuLayoutList size={16} />
          Manage referral statuses
        </DropdownLink>
      )}

      {(isCompanyUser || isGroupUser) && (
        <DropdownLink to={ROUTES.manageUsers()}>
          <LuUsers size={16} />
          Manage users
        </DropdownLink>
      )}

      {isCompanyUser && (
        <DropdownLink to={ROUTES.manageLocations()}>
          <LuMapPin size={16} />
          Manage locations
        </DropdownLink>
      )}

      {isCompanyUser && currentCompany?.hubSpotIntegration && (
        <DropdownLink to={ROUTES.hubSpotIntegration()}>
          <LuLayoutGrid size={16} />
          HubSpot integration
        </DropdownLink>
      )}

      {isCompanyUser && currentCompany?.intellifloIntegration && (
        <DropdownLink to={ROUTES.intellifloIntegration()}>
          <LuLayoutGrid size={16} />
          Intelliflo integration
        </DropdownLink>
      )}

      {isCompanyUser && currentCompany?.plannrIntegration && (
        <DropdownLink to={ROUTES.plannrIntegration()}>
          <LuLayoutGrid size={16} />
          Plannr integration
        </DropdownLink>
      )}

      {isCompanyUser && (
        <>
          <DropdownLink to={ROUTES.referralPagesPage()}>
            <LuLink size={16} />
            Referral pages
          </DropdownLink>

          <DropdownLink to={ROUTES.referralRoutingPage()}>
            <LuRoute size={16} />
            Referral routing
          </DropdownLink>

          <DropdownLink to={ROUTES.calendarSettingsPage()}>
            <LuCalendar size={16} />
            Calendar settings
          </DropdownLink>
        </>
      )}

      <DropdownMenuSeparator />

      {(isCompanyUser || isGroupUser) && (
        <DropdownLink to={ROUTES.help()}>Help</DropdownLink>
      )}

      <DropdownLink to="#" onClick={() => signOut()}>
        Sign out
      </DropdownLink>
    </>
  )
}

const DropdownLink: React.FC<React.ComponentProps<typeof ActionLink>> = ({
  beforeNavigate,
  className,
  ...props
}) => {
  const { setOpenMobile } = useSidebar()

  return (
    <DropdownMenuItem asChild>
      <ActionLink
        beforeNavigate={(event) => {
          if (beforeNavigate) {
            beforeNavigate(event)
          }

          setOpenMobile(false)
        }}
        className={cn('no-underline text-foreground', className)}
        {...props}
      />
    </DropdownMenuItem>
  )
}

export default AccountMenuDropdown
